import React, { createContext, useContext, useEffect, useState } from "react";

/**
 * Three modes:
 *   - "light"  — force light, ignore OS preference
 *   - "dark"   — force dark, ignore OS preference
 *   - "system" — follow `prefers-color-scheme`, switch live if the user
 *                changes OS settings while the tab is open
 *
 * The applied class on `<html>` is always either `dark` or absent — Tailwind
 * resolves `dark:` variants from that. `mode === "system"` resolves to the
 * effective theme each render and subscribes to media-query changes.
 */

export type ThemeMode = "light" | "dark" | "system";
export type EffectiveTheme = "light" | "dark";

interface ThemeContextType {
  /** What the user picked. */
  mode: ThemeMode;
  /** What's actually applied (`mode` resolved against the OS when mode="system"). */
  theme: EffectiveTheme;
  setMode: (mode: ThemeMode) => void;
  /** Convenience used by header toggle: cycle light → dark → system → light. */
  toggleTheme?: () => void;
  switchable: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: EffectiveTheme;
  switchable?: boolean;
}

const STORAGE_KEY = "theme";

function readStoredMode(fallback: ThemeMode): ThemeMode {
  if (typeof window === "undefined") return fallback;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark" || stored === "system") return stored;
  return fallback;
}

function osPrefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function ThemeProvider({
  children,
  defaultTheme = "light",
  switchable = false,
}: ThemeProviderProps) {
  const [mode, setModeState] = useState<ThemeMode>(() =>
    switchable ? readStoredMode(defaultTheme) : defaultTheme,
  );
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(osPrefersDark);

  // Subscribe to OS-level color-scheme changes so "system" flips live.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const listener = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches);
    mq.addEventListener("change", listener);
    return () => mq.removeEventListener("change", listener);
  }, []);

  const effectiveTheme: EffectiveTheme =
    mode === "system" ? (systemPrefersDark ? "dark" : "light") : mode;

  // Apply the class + persist the user's *choice* (not the resolved theme).
  useEffect(() => {
    const root = document.documentElement;
    if (effectiveTheme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    if (switchable) window.localStorage.setItem(STORAGE_KEY, mode);
  }, [mode, effectiveTheme, switchable]);

  const setMode = (next: ThemeMode) => setModeState(next);

  const toggleTheme = switchable
    ? () => {
        // light → dark → system → light
        setModeState(prev =>
          prev === "light" ? "dark" : prev === "dark" ? "system" : "light",
        );
      }
    : undefined;

  return (
    <ThemeContext.Provider
      value={{ mode, theme: effectiveTheme, setMode, toggleTheme, switchable }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
}
